import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpload } from '../context/UploadContext';
import { isNative } from '../native/backgroundMode';
import { formatBytes } from '../lib/ui-utils';
import { X, CheckCircle, AlertCircle, RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';

const UploadIndicator = () => {
  const { upload, cancelUpload, retryUpload, dismissUpload } = useUpload();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  if (!upload) return null;

  const { status, progress, eta, error, files, sessionId, failureStatus } = upload;
  const isRateLimited = failureStatus === 429;
  const fileName = files?.[0]?.name || '';
  const totalSize = files?.reduce((s, f) => s + f.size, 0) || 0;
  const totalSizeLabel = formatBytes(totalSize);

  const formatEta = (seconds) => {
    if (!seconds || seconds < 0) return '';
    if (seconds < 60) return `~${seconds}s left`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `~${m}m ${s}s left`;
  };

  const connType = navigator.connection?.effectiveType?.toUpperCase() || '';

  return (
    <div className="fixed bottom-[60px] left-3 right-3 z-50 max-w-lg mx-auto">
      <div className={`rounded-2xl shadow-lg border backdrop-blur-sm ${
        status === 'complete' ? 'bg-teal-50/95 border-teal-200' :
        status === 'failed' ? 'bg-red-50/95 border-red-200' :
        status === 'cancelled' ? 'bg-card/95 border-border' :
        'bg-card/95 border-border'
      }`}>
        {/* Main bar */}
        <div
          className="px-4 py-3 flex items-center gap-3 cursor-pointer"
          onClick={() => setExpanded(e => !e)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setExpanded(prev => !prev)}
        >
          {status === 'uploading' && (
            <div className="w-5 h-5 shrink-0">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-teal-200 dark:border-teal-800 border-t-teal-600 dark:border-t-teal-400" />
            </div>
          )}
          {status === 'complete' && <CheckCircle size={18} className="text-teal-600 shrink-0" />}
          {status === 'failed' && <AlertCircle size={18} className="text-red-600 shrink-0" />}
          {status === 'cancelled' && <X size={18} className="text-stone-400 shrink-0" />}

          <div className="flex-1 min-w-0">
            {status === 'uploading' && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-foreground truncate">
                  Uploading... {progress}%
                </span>
                {eta > 0 && <span className="text-[10px] text-stone-400 shrink-0">{formatEta(eta)}</span>}
              </div>
            )}
            {status === 'complete' && (
              <span className="text-xs font-medium text-teal-700">
                Upload complete! Analysis running.
              </span>
            )}
            {status === 'failed' && (
              <span className="text-xs font-medium text-red-700 truncate block">
                {error || 'Upload failed'}
              </span>
            )}
            {status === 'cancelled' && (
              <span className="text-xs font-medium text-stone-500">
                Upload cancelled
              </span>
            )}
            {status === 'uploading' && (
              <>
                <div className="w-full bg-stone-200 dark:bg-stone-700 rounded-full h-1.5 mt-1.5 overflow-hidden">
                  <div className="h-full bg-teal-500 dark:bg-teal-400 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
                {isNative() && (
                  <p className="text-[10px] text-teal-600 mt-1">
                    You can close the app -- upload will continue in background
                  </p>
                )}
              </>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1.5 shrink-0">
            {status === 'complete' && sessionId && (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/session/${sessionId}`); dismissUpload(); }}
                className="text-[10px] font-semibold text-teal-700 bg-teal-100 px-3 py-1 rounded-full"
              >
                View
              </button>
            )}
            {status === 'failed' && !isRateLimited && (
              <button
                onClick={(e) => { e.stopPropagation(); retryUpload(); }}
                className="text-[10px] font-semibold text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30 px-3 py-1 rounded-full flex items-center gap-1"
              >
                <RefreshCw size={10} /> Retry
              </button>
            )}
            {status !== 'uploading' && (
              <button
                onClick={(e) => { e.stopPropagation(); dismissUpload(); }}
                className="p-1 text-stone-400 hover:text-foreground transition-colors"
              >
                <X size={14} />
              </button>
            )}
            {expanded ? <ChevronDown size={14} className="text-stone-400" /> : <ChevronUp size={14} className="text-stone-400" />}
          </div>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="px-4 pb-3 border-t border-border pt-2 space-y-1">
            <p className="text-[11px] text-stone-500 truncate">
              {fileName}{files?.length > 1 ? ` +${files.length - 1}` : ''}
            </p>
            <p className="text-[11px] text-stone-500">
              {totalSizeLabel}{connType ? ` \u00b7 ${connType}` : ''}
            </p>
            {status === 'uploading' && (
              <button onClick={cancelUpload} className="text-[11px] text-red-600 font-medium mt-1">
                Cancel upload
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadIndicator;
